"use client";

import {
  Badge,
  cn,
  Progress,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@devhop/ui";
import {
  CheckCircle2Icon,
  CircleDashedIcon,
  CircleDotIcon,
  ClockIcon,
  InfoIcon,
  LayersIcon,
  RocketIcon,
} from "lucide-react";
import { PublicLayout } from "@/app/layout/PublicLayout";
import {
  countTasksByStatus,
  DEVELOPMENT_DEPLOY_ALERT,
  DEVELOPMENT_META,
  DEVELOPMENT_PLATFORMS,
  overallProgress,
  STATUS_LABEL,
  type DeployAlertSection,
  type DevStatus,
  type DevTask,
} from "../../domain/status-data";

const STATUS_ICON: Record<DevStatus, typeof CheckCircle2Icon> = {
  done: CheckCircle2Icon,
  in_progress: ClockIcon,
  partial: CircleDotIcon,
  planned: CircleDashedIcon,
};

const STATUS_BADGE_VARIANT: Record<
  DevStatus,
  "default" | "secondary" | "outline" | "destructive"
> = {
  done: "default",
  in_progress: "secondary",
  partial: "outline",
  planned: "outline",
};

const STATUS_COLOR: Record<DevStatus, string> = {
  done: "text-emerald-600 dark:text-emerald-400",
  in_progress: "text-blue-600 dark:text-blue-400",
  partial: "text-amber-600 dark:text-amber-400",
  planned: "text-muted-foreground",
};

function StatusBadge({ status }: { status: DevStatus }) {
  const Icon = STATUS_ICON[status];
  return (
    <Badge variant={STATUS_BADGE_VARIANT[status]} className="gap-1 font-normal">
      <Icon className="h-3 w-3" />
      {STATUS_LABEL[status]}
    </Badge>
  );
}

function TaskRow({ task }: { task: DevTask }) {
  const Icon = STATUS_ICON[task.status];
  return (
    <li className="flex items-start gap-3 rounded-lg border bg-card px-3 py-2.5">
      <Icon
        className={cn("mt-0.5 h-4 w-4 shrink-0", STATUS_COLOR[task.status])}
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm leading-snug">{task.title}</p>
        {task.note ? (
          <p className="mt-0.5 text-muted-foreground text-xs">{task.note}</p>
        ) : null}
      </div>
      <StatusBadge status={task.status} />
    </li>
  );
}

export function DevelopmentStatusPage() {
  const taskCounts = countTasksByStatus(DEVELOPMENT_PLATFORMS);
  const totalTasks = Object.values(taskCounts).reduce((a, b) => a + b, 0);
  const progress = overallProgress(DEVELOPMENT_PLATFORMS);

  return (
    <PublicLayout>
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10">
        <div className="mb-8">
          <div className="mb-2 flex items-center gap-2 text-muted-foreground text-sm">
            <LayersIcon className="h-4 w-4" />
            <span>ຕິດຕາມຄວາມຄືບໜ້າການພັດທະນາ</span>
          </div>
          <h1 className="mb-2 font-bold font-heading text-3xl tracking-tight sm:text-4xl">
            {DEVELOPMENT_META.title}
          </h1>
          <p className="max-w-2xl text-muted-foreground">{DEVELOPMENT_META.subtitle}</p>
          <p className="mt-2 text-muted-foreground text-xs">
            ອັບເດດລ່າສຸດ: {DEVELOPMENT_META.lastUpdated}
          </p>
        </div>

        <DeployAlertBanner alert={DEVELOPMENT_DEPLOY_ALERT} />

        <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <SummaryCard
            label="ຄວາມຄືບໜ້າລວມ"
            value={`${progress}%`}
            className="sm:col-span-2 lg:col-span-1"
          />
          <SummaryCard
            label="ເຮັດແລ້ວ"
            value={String(taskCounts.done)}
            sub={`ຈາກ ${totalTasks} ລາຍການ`}
            accent="text-emerald-600 dark:text-emerald-400"
          />
          <SummaryCard
            label="ບາງສ່ວນ"
            value={String(taskCounts.partial)}
            accent="text-amber-600 dark:text-amber-400"
          />
          <SummaryCard
            label="ກຳລັງເຮັດ"
            value={String(taskCounts.in_progress)}
            accent="text-blue-600 dark:text-blue-400"
          />
          <SummaryCard
            label="ຍັງບໍ່ເລີ່ມ"
            value={String(taskCounts.planned)}
          />
        </div>

        <div className="mb-8 rounded-xl border bg-card p-4 sm:p-6">
          <div className="mb-3 flex items-center justify-between gap-4">
            <h2 className="font-semibold text-lg">ຄວາມຄືບໜ້າແຕ່ລະ Platform</h2>
            <span className="font-medium text-muted-foreground text-sm">
              {progress}% ລວມ
            </span>
          </div>
          <Progress value={progress} className="mb-4 h-2" />
          <div className="grid gap-3 sm:grid-cols-3">
            {DEVELOPMENT_PLATFORMS.map((platform) => (
              <div
                key={platform.id}
                className="rounded-lg border bg-background p-3"
              >
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="font-medium text-sm">{platform.title}</span>
                  <span className="text-muted-foreground text-xs">
                    {platform.progress}%
                  </span>
                </div>
                <Progress value={platform.progress} className="h-1.5" />
              </div>
            ))}
          </div>
        </div>

        <Tabs defaultValue={DEVELOPMENT_PLATFORMS[0]?.id ?? "admin"}>
          <TabsList className="mb-6 h-auto w-full flex-wrap justify-start gap-1">
            {DEVELOPMENT_PLATFORMS.map((platform) => (
              <TabsTrigger
                key={platform.id}
                value={platform.id}
                className="gap-2"
              >
                {platform.title}
                <Badge variant="secondary" className="px-1.5 py-0 text-xs">
                  {platform.progress}%
                </Badge>
              </TabsTrigger>
            ))}
          </TabsList>

          {DEVELOPMENT_PLATFORMS.map((platform) => (
            <TabsContent key={platform.id} value={platform.id} className="space-y-6">
              <div className="rounded-xl border bg-muted/30 p-4 sm:p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="font-semibold text-xl">{platform.title}</h2>
                    <p className="mt-1 text-muted-foreground text-sm">
                      {platform.subtitle}
                    </p>
                  </div>
                  <StatusBadge status={platform.status} />
                </div>
                <div className="mt-4">
                  <Progress value={platform.progress} className="h-2" />
                </div>
              </div>

              <div className="grid gap-5 lg:grid-cols-2">
                {platform.modules.map((mod) => (
                  <section
                    key={mod.id}
                    className="rounded-xl border bg-card p-4 sm:p-5"
                  >
                    <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <h3 className="font-semibold">{mod.title}</h3>
                        {mod.description ? (
                          <p className="mt-0.5 text-muted-foreground text-xs">
                            {mod.description}
                          </p>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground text-xs">
                          {mod.progress}%
                        </span>
                        <StatusBadge status={mod.status} />
                      </div>
                    </div>
                    <Progress value={mod.progress} className="mb-4 h-1.5" />
                    <ul className="space-y-2">
                      {mod.tasks.map((task) => (
                        <TaskRow key={task.id} task={task} />
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>

        <div className="mt-10 rounded-xl border border-dashed p-4 text-center text-muted-foreground text-sm">
          <p>
            ໜ້ານີ້ເປັນໜ້າ public — ບໍ່ຕ້ອງ login ເພື່ອເບິ່ງສະຖານະການພັດທະນາ
          </p>
          <p className="mt-1 text-xs">
            ຂໍ້ມູນອ້າງອີງຈາກ roadmap ແລະ ເອກະສານ implement ໃນ repo
          </p>
        </div>
      </div>
    </PublicLayout>
  );
}

function DeployAlertBanner({ alert }: { alert: typeof DEVELOPMENT_DEPLOY_ALERT }) {
  return (
    <section
      aria-labelledby="deploy-alert-title"
      className="mb-8 rounded-xl border border-sky-500/40 bg-sky-500/5 p-4 sm:p-5"
    >
      <div className="mb-3 flex flex-wrap items-start gap-3">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-sky-500/15 text-sky-700 dark:text-sky-300">
          <RocketIcon className="size-5" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-sky-800 text-xs uppercase tracking-wide dark:text-sky-200">
            Deploy alert · {alert.deployedAt}
          </p>
          <h2
            id="deploy-alert-title"
            className="mt-0.5 font-semibold text-base sm:text-lg"
          >
            {alert.title}
          </h2>
          <p className="mt-1 flex items-start gap-1.5 text-muted-foreground text-sm">
            <InfoIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
            {alert.summary}
          </p>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {alert.sections.map((section) => (
          <DeployAlertSectionBlock key={section.title} section={section} />
        ))}
      </div>
    </section>
  );
}

function DeployAlertSectionBlock({ section }: { section: DeployAlertSection }) {
  return (
    <div className="rounded-lg border border-border/80 bg-background/80 p-3">
      <h3 className="mb-2 font-medium text-sm">{section.title}</h3>
      <ul className="space-y-1.5 text-sm">
        {section.items.map((item) => (
          <li key={item} className="flex gap-2 leading-snug">
            <span
              className="mt-2 size-1.5 shrink-0 rounded-full bg-sky-500"
              aria-hidden
            />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  sub,
  accent,
  className,
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: string;
  className?: string;
}) {
  return (
    <div className={cn("rounded-xl border bg-card p-4", className)}>
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className={cn("mt-1 font-bold text-2xl tabular-nums", accent)}>
        {value}
      </p>
      {sub ? (
        <p className="mt-0.5 text-muted-foreground text-xs">{sub}</p>
      ) : null}
    </div>
  );
}
